import React from "react";
import { View, StyleSheet, Pressable } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { TIME_MODIFIER_CODES, TIME_MODIFIERS, type TimeModifierCode } from "@/types/db/time_cards";

type CompactModifierPickerProps = {
  selectedModifiers: TimeModifierCode[];
  onModifiersChange: (modifiers: TimeModifierCode[]) => void;
};

export function CompactModifierPicker({ selectedModifiers, onModifiersChange }: CompactModifierPickerProps) {
  const { colors } = useTheme();

  const toggleModifier = (code: TimeModifierCode) => {
    const newSelection = selectedModifiers.includes(code)
      ? selectedModifiers.filter((c) => c !== code)
      : [...selectedModifiers, code];
    onModifiersChange(newSelection);
  };

  return (
    <View style={styles.container}>
      {/* Modifier list */}
      <View style={styles.list}>
        {TIME_MODIFIER_CODES.map((code) => {
          const modifier = TIME_MODIFIERS[code];
          const isSelected = selectedModifiers.includes(code);

          return (
            <Pressable
              key={code}
              style={[
                styles.row,
                { borderColor: colors.border },
                isSelected && { backgroundColor: `${modifier.color}15` },
              ]}
              onPress={() => toggleModifier(code)}
            >
              {/* Checkbox indicator - color indicates modifier */}
              <View
                style={[
                  styles.checkbox,
                  { borderColor: modifier.color },
                  isSelected && { backgroundColor: modifier.color },
                ]}
              >
                {isSelected && <ThemedText style={styles.checkmark}>✓</ThemedText>}
              </View>

              {/* Stacked label: code on top, full label below */}
              <View style={styles.labelContainer}>
                <ThemedText style={[styles.label, isSelected && { fontWeight: "600" }]}>{code}</ThemedText>
                <ThemedText style={[styles.sublabel, { color: colors.textSecondary }]}>{modifier.label}</ThemedText>
              </View>

              {/* Multiplier badge */}
              <View style={[styles.multiplierBadge, { backgroundColor: `${modifier.color}25` }]}>
                <ThemedText style={[styles.multiplier, { color: modifier.color }]}>{modifier.multiplier}x</ThemedText>
              </View>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: 220,
    gap: 8,
  },
  list: {
    gap: 4,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 6,
    borderWidth: StyleSheet.hairlineWidth,
  },
  checkbox: {
    width: 16,
    height: 16,
    borderRadius: 3,
    borderWidth: 1.5,
    alignItems: "center",
    justifyContent: "center",
  },
  checkmark: {
    color: "white",
    fontSize: 10,
    fontWeight: "700",
    lineHeight: 12,
  },
  labelContainer: {
    flex: 1,
  },
  label: {
    fontSize: 12,
    lineHeight: 14,
  },
  sublabel: {
    fontSize: 9,
    lineHeight: 10,
  },
  multiplierBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  multiplier: {
    fontSize: 10,
    fontWeight: "600",
  },
});
